import { useEffect, useState } from 'react';
import api from '@/lib/api';

/**
 * Global theme spec per plan tier. Every Zertivo dashboard surface uses
 * these to render a plan-aware accent — the user should feel which tier
 * they are on without reading the name.
 *
 * Palette rules:
 *   • free        — neutral zinc, no shine (keeps upgrade CTAs salient)
 *   • starter     — signature brand green; the "standard" experience
 *   • professional— amber / rose-gold for "premium but not enterprise"
 *   • enterprise  — void-black + platinum; cinematic, bespoke
 *
 * We deliberately avoid purple/violet → stays out of "AI slop" territory.
 */
export const PLAN_THEMES = {
  free: {
    key: 'free',
    label: 'FREE',
    name: 'Free',
    accent: '#71717A',
    accentBg: '#F4F4F5',
    accentInk: '#27272A',
    gradient: 'from-[#71717A] to-[#27272A]',
    softGradient: 'from-[#F4F4F5] to-[#E4E4E7]',
    ringClass: 'ring-[#E4E4E7]',
    badgeClass: 'bg-[#F4F4F5] text-[#27272A] border-[#E4E4E7]',
    glyph: 'FREE',
  },
  starter: {
    key: 'starter',
    label: 'STARTER',
    name: 'Starter',
    accent: '#00D387',
    accentBg: '#E6FBF3',
    accentInk: '#065F43',
    gradient: 'from-[#0F8A5F] to-[#00D387]',
    softGradient: 'from-[#E6FBF3] to-[#D1F5E5]',
    ringClass: 'ring-[#A7F3D0]',
    badgeClass: 'bg-[#E6FBF3] text-[#065F43] border-[#A7F3D0]',
    glyph: 'S',
  },
  professional: {
    key: 'professional',
    label: 'PRO',
    name: 'Professional',
    accent: '#E8A268',
    accentBg: '#FEF3E7',
    accentInk: '#8B4A22',
    gradient: 'from-[#A37916] to-[#FFD976]',
    softGradient: 'from-[#FEF3E7] to-[#FFE8CC]',
    ringClass: 'ring-[#F5D0A9]',
    badgeClass: 'bg-gradient-to-br from-[#FEF3E7] to-[#FFE8CC] text-[#8B4A22] border-[#F5D0A9]',
    glyph: 'P',
  },
  enterprise: {
    key: 'enterprise',
    label: 'ENTERPRISE',
    name: 'Enterprise',
    accent: '#CFEEFF',
    accentBg: '#0A0F1F',
    accentInk: '#E4E4E7',
    gradient: 'from-[#09090B] via-[#1A1F2E] to-[#4A7A96]',
    softGradient: 'from-[#0A0F1F] to-[#1A1F2E]',
    ringClass: 'ring-[#4A7A96]',
    badgeClass: 'bg-[#09090B] text-[#CFEEFF] border-[#1F2433]',
    glyph: 'E',
  },
};

export function getPlanTheme(planKey) {
  return PLAN_THEMES[planKey] || PLAN_THEMES.free;
}

/**
 * Fetch the logged-in user's active plan + feature flags. Public cached
 * module variable so multiple components do not hammer `/plan/me` on mount.
 */
let _cachedPlan = null;
let _cachedPlanAt = 0;
const TTL_MS = 60_000;
// PubSub: every mounted `usePlan` subscribes here so a single `refresh()`
// invalidates the cache across the whole app (nav, dashboard, billing page).
const _subscribers = new Set();

/** Force a fresh fetch of `/plan/me` everywhere the hook is mounted. */
export function refreshPlanCache() {
  _cachedPlan = null;
  _cachedPlanAt = 0;
  _subscribers.forEach((fn) => { try { fn(); } catch (_) { /* noop */ } });
}

export default function usePlan() {
  const [data, setData] = useState(() =>
    _cachedPlan && (Date.now() - _cachedPlanAt < TTL_MS) ? _cachedPlan : null
  );
  const [loading, setLoading] = useState(!data);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // Register as a subscriber so cross-component refresh() bumps us too.
    const bump = () => { setData(null); setTick((n) => n + 1); };
    _subscribers.add(bump);
    return () => { _subscribers.delete(bump); };
  }, []);

  useEffect(() => {
    if (data && Date.now() - _cachedPlanAt < TTL_MS) return;
    let alive = true;
    setLoading(true);
    api.get('/plan/me')
      .then((r) => {
        if (!alive) return;
        _cachedPlan = r.data;
        _cachedPlanAt = Date.now();
        setData(r.data);
      })
      .catch(() => {
        if (!alive) return;
        setData({ plan_key: 'free', plan_name: 'Free', is_enterprise: false, features: {} });
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // `tick` forces a re-fetch when refreshPlanCache() pings the subscribers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tick]);

  const theme = getPlanTheme(data?.plan_key);

  return {
    plan: data,
    loading,
    planKey: data?.plan_key || 'free',
    isEnterprise: data?.plan_key === 'enterprise',
    features: data?.features || {},
    theme,
    /** Invalidate the cache — call after plan change events. Global. */
    refresh: refreshPlanCache,
  };
}
